import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { base44 } from '@/api/base44Client';

const loadImageAsBase64 = async (url) => {
  if (!url) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = async () => {
      try {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      } catch {
        resolve(null);
      }
    };
    img.src = url;
  });
};

export const exportToPDF = async ({ title, subtitle, filename, columns, rows, orientation = 'landscape' }) => {
  // Buka window kosong dulu agar tidak diblock popup blocker karena ada proses async
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write('<div style="font-family: sans-serif; text-align: center; margin-top: 20%; color: #666;">Membuat Dokumen PDF...</div>');
  }

  // Ambil pengaturan perusahaan
  let companySettings = {};
  let logoB64 = null;
  try {
    const settingsList = await base44.entities.CompanySettings.list();
    companySettings = settingsList?.[0] || {};
    if (companySettings.logo_url) {
      logoB64 = await loadImageAsBase64(companySettings.logo_url);
    }
  } catch (e) {
    console.warn("Gagal memuat company settings", e);
  }

  if (!logoB64) {
    try {
      logoB64 = await loadImageAsBase64('/logo-invoice.png');
    } catch (e) {
      console.warn("Gagal memuat logo fallback", e);
    }
  }

  const doc = new jsPDF(orientation);
  
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginL = 14;
  const marginR = 14;
  
  let y = 15;

  // Header Perusahaan
  if (logoB64) {
    // Logo proporsional (persegi) seperti di invoice preview
    doc.addImage(logoB64, 'PNG', marginL, y - 2, 14, 14, '', 'FAST');
  } else {
    doc.setFillColor(180, 140, 80);
    doc.circle(marginL + 7, y + 5, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('S', marginL + 5.5, y + 8.5);
  }

  const namaPerusahaan = companySettings.nama_perusahaan || 'PT. Satala Dermatech Essential';
  const alamat = companySettings.alamat_perusahaan || 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat';

  doc.setTextColor(30, 20, 10);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(namaPerusahaan, marginL + 18, y + 4);
  
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(alamat, marginL + 18, y + 9);

  y += 16;
  
  // Divider Line
  doc.setDrawColor(180, 140, 60);
  doc.setLineWidth(0.5);
  doc.line(marginL, y, pageW - marginR, y);
  
  y += 8;

  // Title
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 20, 10);
  doc.text(title || 'Laporan', marginL, y);
  y += 6;
  
  // Subtitle
  if (subtitle) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 80, 60);
    doc.text(subtitle, marginL, y);
    y += 6;
  }
  
  // Table headers
  const head = [columns.map(col => col.label)];
  
  // Table body
  const body = rows.map(row => {
    return columns.map(col => {
      if (col.render) {
        return col.render(row) || '-';
      }
      return row[col.key] || '-';
    });
  });
  
  autoTable(doc, {
    head: head,
    body: body,
    startY: y,
    theme: 'grid',
    styles: { 
      fontSize: 8,
      font: 'helvetica',
      textColor: [30, 20, 10],
      lineColor: [220, 210, 200],
      lineWidth: 0.1,
    },
    headStyles: { 
      fillColor: [60, 40, 20], 
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center'
    },
    alternateRowStyles: {
      fillColor: [250, 247, 242]
    },
    bodyStyles: {
      fillColor: [255, 255, 255]
    },
    margin: { left: marginL, right: marginR },
    didDrawPage: (data) => {
      // Footer
      const str = 'Halaman ' + doc.internal.getNumberOfPages();
      const dateStrPrint = 'Dicetak pada: ' + new Date().toLocaleString('id-ID');
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      
      const pageHeight = doc.internal.pageSize.height || doc.internal.pageSize.getHeight();
      doc.text(dateStrPrint, data.settings.margin.left, pageHeight - 10);
      doc.text(str, doc.internal.pageSize.width - data.settings.margin.right, pageHeight - 10, { align: 'right' });
    }
  });
  
  // Buka dialog Print (Preview) di tab yang sudah disiapkan
  const dateStr = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  doc.setProperties({
    title: `${filename || 'export'}_${dateStr}.pdf`
  });
  
  doc.autoPrint();
  const blobUrl = doc.output('bloburl');
  
  if (printWindow) {
    printWindow.location.href = blobUrl;
  } else {
    window.open(blobUrl, '_blank'); // Fallback jika window diblokir sejak awal
  }
};
